import {
  KODEM_SESSION_LS,
  LAST_SUCCESS_SLUG_LOG_IN,
  LAST_SUCCESS_TIME_LOG_IN,
  SLUG_LS
} from "@/const/local-storage.constants"
import { toLowerCase } from "@/helpers/string.helpers"
import { FirebaseOptions, initializeApp } from "@firebase/app"
import {
  Auth,
  confirmPasswordReset,
  User as FirebaseUser,
  getAuth,
  getMultiFactorResolver,
  GithubAuthProvider,
  GoogleAuthProvider,
  multiFactor,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneInfoOptions,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  SAMLAuthProvider,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup
} from "@firebase/auth"
import { BaseAuthAdapter } from "./auth-adapter.base"
import {
  exchangeKodemSsoToken,
  signInWithKodemPassword
} from "./kodem-password"

import type { User } from "@/domain/user"
import type { Services } from "@/interfaces/services"

export type InitialParamsAuthAdapter = {
  slug?: string
} & FirebaseOptions

export class AuthAdapter
  extends BaseAuthAdapter
  implements Services.IAuthAdapter
{
  static defaultInstance: AuthAdapter

  private app: Auth

  private recaptchaVerifier: RecaptchaVerifier

  private resolver: MultiFactorResolver | undefined

  protected currentUser: User

  protected company: string

  private resolveToken: ((value: string | undefined) => void) | null = null

  private token: Promise<string | undefined>

  private subscribers: Record<string, Array<(data: any) => void>> = {}

  private multiFactorAuthEnabled = false

  private kodemAuthBackend = false

  public AUTH_STATE_CHANGED_EVENT = "authStateChanged"

  public AUTH_MULTI_FACTOR_EVENT = "authMultiFactor"

  public AUTH_TRIAL_DAYS_EXPIRED = "authTrialDaysExpired"

  constructor({ slug, ...options }: InitialParamsAuthAdapter) {
    super()
    this.app = getAuth(initializeApp(options))
    this.recaptchaVerifier = new RecaptchaVerifier(
      this.app,
      "recaptcha-container",
      { size: "invisible" }
    )
    this.company = slug || ""
    this.currentUser = this.createUser({
      company: this.company,
      authorized: !!slug
    })

    this.token = new Promise((resolvePromise) => {
      this.resolveToken = resolvePromise
    })

    this.app.onAuthStateChanged(this.onAuthStateChanged)
  }

  public trialDaysExpired = (resolve: (value: unknown) => void) => {
    this.publish(this.AUTH_TRIAL_DAYS_EXPIRED, resolve)
  }

  private sendVerificationCode = async (phoneInfoOptions: PhoneInfoOptions) => {
    if (this.app.currentUser) {
      const phoneAuthProvider = new PhoneAuthProvider(this.app)

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        this.recaptchaVerifier
      )

      return verificationId
    }
  }

  public sendCodePhoneNumberEnrolling = async (phoneNumber: string) => {
    if (!this.app.currentUser || !phoneNumber) {
      return null
    }

    const multiFactorSession = await multiFactor(
      this.app.currentUser
    ).getSession()

    return await this.sendVerificationCode({
      phoneNumber,
      session: multiFactorSession
    })
  }

  sendVerificationCodeOnPhoneNumber = async () => {
    if (!this.resolver) {
      return null
    }

    const phoneInfoOptions = {
      multiFactorHint: this.resolver.hints[0],
      session: this.resolver.session
    }
    const phoneAuthProvider = new PhoneAuthProvider(this.app)

    return await phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      this.recaptchaVerifier
    )
  }

  private onAuthStateChanged = async (user: FirebaseUser | null) => {
    // A Kodem session has no Firebase user, it must not be signed out for lacking one
    if (user) localStorage.removeItem(KODEM_SESSION_LS)
    if (user || !this.isKodemSession()) this.setCurrentUser(user)
    this.publish(this.AUTH_STATE_CHANGED_EVENT, this.getCurrentUser())

    const token = await user?.getIdToken()

    if (this.resolveToken) {
      this.resolveToken(token)
      this.resolveToken = null
    } else {
      this.token = Promise.resolve(token)
    }
  }

  private publish(eventName: string, data: unknown) {
    if (!Array.isArray(this.subscribers[eventName])) {
      return
    }

    this.subscribers[eventName].forEach((callback) => {
      callback(data)
    })
  }

  public setLastSuccessLoginTime = () => {
    localStorage.setItem(LAST_SUCCESS_TIME_LOG_IN, Date.now().toString())
  }

  public subscribe(eventName: string, callback: (data: any) => void) {
    if (!this.subscribers[eventName]) {
      this.subscribers[eventName] = []
    }

    this.subscribers[eventName].push(callback)

    return () => {
      this.subscribers[eventName] = this.subscribers[eventName].filter((cb) => {
        if (cb === callback) {
          return false
        }

        return true
      })
    }
  }

  public setCompany = (company: string) => {
    this.company = toLowerCase(company)
  }

  public setMultiFactorAuth = (multiFactorAuthEnabled: boolean) => {
    this.multiFactorAuthEnabled = multiFactorAuthEnabled
  }

  public setTenantId = (tenantId: string | null) => {
    this.app.tenantId = tenantId
  }

  // A tenant on the Kodem auth backend checks passwords in the API instead of Firebase
  public setKodemAuthBackend = (kodemAuthBackend: boolean) => {
    this.kodemAuthBackend = kodemAuthBackend
  }

  public getCurrentUser = () => this.currentUser

  public getCompany = () => this.company

  public getRecaptcha = () => this.recaptchaVerifier

  public verificationCode = async (
    verificationId: string,
    verificationCode: string,
    enrolling?: boolean
  ) => {
    const errorMessage = "verificationCode-auth-adapter: INVALID-PARAMS"

    if (!verificationId || !verificationCode) {
      throw new Error(errorMessage)
    }

    const cred = PhoneAuthProvider.credential(verificationId, verificationCode)
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)

    if (enrolling) {
      if (!this.app.currentUser) {
        throw new Error(errorMessage)
      }

      return await multiFactor(this.app.currentUser).enroll(
        multiFactorAssertion
      )
    } else {
      if (!this.resolver) {
        return null
      }

      await this.resolver.resolveSignIn(multiFactorAssertion)

      this.onSuccessSignIn()
      this.clearCaptcha()
    }
  }

  private onSuccessSignIn = () => {
    localStorage.setItem(SLUG_LS, this.company)
    this.setLastSuccessLoginTime()
  }

  onSignIn: any = async () => {
    try {
      if (this.multiFactorAuthEnabled) {
        this.publish(this.AUTH_MULTI_FACTOR_EVENT, {
          userNeedVerifyPhoneNumberMultiFactor: true
        })
      }

      this.setCurrentUser(this.app.currentUser)

      const user = this.getCurrentUser()

      if (user.authorized && this.company) this.onSuccessSignIn()

      return this.currentUser
    } catch {
      return this.currentUser
    }
  }

  public skipMultiFactorSetUp = () => {
    this.publish(this.AUTH_MULTI_FACTOR_EVENT, {
      userNeedVerifyPhoneNumberMultiFactor: false
    })
  }

  getToken = async () => {
    try {
      if (this.resolveToken) {
        await this.token
      }

      const token = await this.app.currentUser?.getIdToken()

      return token
    } catch {
      return undefined
    }
  }

  private clearCaptcha = () => {
    try {
      this.getRecaptcha().clear()
    } catch {
      return null
    }
  }

  private checkSignInError = (error: any) => {
    if (error?.code === "auth/multi-factor-auth-required") {
      this.publish(this.AUTH_MULTI_FACTOR_EVENT, {
        userNeedVerificationCodeMultiFactor: true
      })
      this.resolver = getMultiFactorResolver(this.app, error)

      return
    }

    if (error?.code === "auth/invalid-login-credentials") {
      return { error: true, translationKey: "invalid-login-credentials" }
    }

    throw error
  }

  signInWithSaml = async (firebaseProviderId = "saml.google-workspace") => {
    try {
      const provider = new SAMLAuthProvider(firebaseProviderId)

      await signInWithPopup(this.app, provider)

      return this.onSignIn()
    } catch (error) {
      return this.checkSignInError(error)
    }
  }

  signInWithToken = async (token: string) => {
    try {
      await signInWithCustomToken(this.app, token)

      return this.onSignIn()
    } catch (error) {
      return this.checkSignInError(error)
    }
  }

  public isKodemSession = () =>
    localStorage.getItem(KODEM_SESSION_LS) === "true"

  // Exchanges the handoff token of a Kodem SSO login for the API session cookies
  signInWithSsoToken = async (slug: string, ssoToken: string) => {
    // Firebase's first auth state comes before the session is stored: arriving after it, it would
    // announce the stored session and start the app in the popup
    await this.getToken()

    const email = await exchangeKodemSsoToken(slug, ssoToken)

    this.setCompany(slug)

    // Not announced: this runs in the SSO popup, which hands the sign-in over to the sign-in page
    // and closes. The app starting here would have its requests aborted by the close, and the
    // resulting sign-out clears the session the windows share
    return this.storeKodemSession(email)
  }

  private storeKodemSession = (email: string | null) => {
    localStorage.setItem(KODEM_SESSION_LS, "true")
    this.setCurrentUser({ email })
    this.onSuccessSignIn()

    return this.currentUser
  }

  private startKodemSession = (email: string | null) => {
    this.storeKodemSession(email)
    // No Firebase state change follows, so announce the sign-in for the app to take over
    this.publish(this.AUTH_STATE_CHANGED_EVENT, this.getCurrentUser())

    return this.currentUser
  }

  signInWithGoogle = async () => {
    const googleProvider = new GoogleAuthProvider()

    googleProvider.addScope("profile")
    googleProvider.addScope("email")

    await signInWithPopup(this.app, googleProvider)

    return this.onSignIn()
  }

  signInWithGithub = async () => {
    const githubProvider = new GithubAuthProvider()

    githubProvider.addScope("profile")
    githubProvider.addScope("email")

    await signInWithPopup(this.app, githubProvider)

    return this.onSignIn()
  }

  signInWithEmailPass = async (email: string, password: string) => {
    if (this.kodemAuthBackend) {
      const user = await signInWithKodemPassword(this.company, email, password)

      if (!user)
        return { error: true, translationKey: "invalid-login-credentials" }

      return this.startKodemSession(user.email)
    }

    try {
      await signInWithEmailAndPassword(this.app, email, password)

      return this.onSignIn()
    } catch (error) {
      return this.checkSignInError(error)
    }
  }

  signOut = async () => {
    const wasKodemSession = this.isKodemSession()

    localStorage.removeItem(KODEM_SESSION_LS)
    await this.app.signOut()
    this.company = ""
    this.currentUser = this.createUser({ authorized: false })

    // No Firebase state change follows, so announce it for the API session to be logged out
    if (wasKodemSession)
      this.publish(this.AUTH_STATE_CHANGED_EVENT, this.currentUser)
  }

  handleResetPassword = async (code: string, newPassword: string) => {
    try {
      await confirmPasswordReset(this.app, code, newPassword)

      return [true, undefined]
    } catch (err) {
      return [false, err as any]
    }
  }

  isDemo = () => {
    return this.company === "kodemsecurity"
  }

  isInternalTenant = () => {
    return (
      this.company === "kodemsec" ||
      this.company === "kodemsec-qa" ||
      this.company === "sampledata" ||
      this.isDemo()
    )
  }

  public syncUserWithAuthResponse = async (email?: string) => {
    if (email && this.company)
      localStorage.setItem(LAST_SUCCESS_SLUG_LOG_IN, this.company)

    // Announced only when the email is new (after a page load): every cookie renewal comes through
    // here, and each announcement is a sign-in for the app
    if (email && this.isKodemSession() && email !== this.currentUser.email) {
      this.setCurrentUser({ email })
      this.publish(this.AUTH_STATE_CHANGED_EVENT, this.getCurrentUser())
    }
  }
}
